import React, { useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { useDateTimeFormatter } from '@atlassian/jira-issue-format-date/src/main.tsx';
import type { dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView$key as DateTimeReadViewFragment } from '@atlassian/jira-relay/src/__generated__/dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView.graphql';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { HoverPopoverWithContext } from '@atlassian/jira-hover-popover/src/ui/popover-with-context/index.tsx';
import { HoverPopoverWithRelay } from '@atlassian/jira-hover-popover/src/ui/popover-with-relay/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Props } from './types.tsx';

/**
 * DateTimeReadView component lets you view the date and time of a timestammp in the timeZone the current user is in.
 * In Case timeZone is passed as a prop in it adhears to prop timeZone
 *
 * We later need to fireUIAnalytics when an error occurs while formatting the date time
 * also discussions for removing timeZone is available here https://atlassian.slack.com/archives/CFG7SUPJM/p1665391419261039
 *
 * @param props {@link DateTimeReadView}
 * */
export const DateTimeReadView = (props: Props) => {
	const {
		fragmentRef,
		timeZone: overrideTimeZone,
		isDateOnlyFormat = false,
		shouldRenderHoverPopover = false,
	} = props;
	const data = useFragment<DateTimeReadViewFragment>(
		graphql`
			fragment dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView on JiraDateTimePickerField
			@argumentDefinitions(
				isSingleLineEventDelegatorEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-single-line-event-delegator-enabled.relayprovider"
				}
			) {
				dateTime
				hoverOrFocusState @include(if: $isSingleLineEventDelegatorEnabled) {
					...popoverWithRelay_hoverPopover_HoverPopoverWithRelayComponent
				}
			}
		`,
		fragmentRef,
	);
	const { dateTime } = data || {};
	const { formatDateTime, formatDateOnly } = useDateTimeFormatter(overrideTimeZone);

	const contentRef = useRef(null);

	if (!dateTime) {
		return <EmptyFieldText />;
	}

	let content;

	try {
		content = isDateOnlyFormat ? formatDateOnly(dateTime) : formatDateTime(dateTime);
	} catch (err) {
		return <></>;
	}

	if (shouldRenderHoverPopover && dateTime) {
		return fg('issue_table_single_line_row_height_event_delegator') ? (
			<HoverPopoverWithRelay
				contentRef={contentRef}
				content={content}
				fragmentRef={data?.hoverOrFocusState}
			>
				{({ ref }) => (
					<Text maxLines={1} ref={mergeRefs(ref, contentRef)}>
						{content}
					</Text>
				)}
			</HoverPopoverWithRelay>
		) : (
			<HoverPopoverWithContext contentRef={contentRef} content={content}>
				{({ ref }) => (
					<Text maxLines={1} ref={mergeRefs(ref, contentRef)}>
						{content}
					</Text>
				)}
			</HoverPopoverWithContext>
		);
	}

	return content;
};
